import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Outlet } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  InputAdornment,
  Tooltip,
  FormControlLabel,
  Switch,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Collections as CollectionsIcon,
  Settings as SettingsIcon,
  Crop as CropIcon,
  ContentCopy as ContentCopyIcon,
  Link as LinkIcon,
  History as VintageIcon,
  AutoMode as AutoModeIcon,
  Image as ImageIcon,
  TextFields as TextFieldsIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import api, { influencerAPI } from '../services/api/apiService';
import { useImages } from '../context/images/ImagesContext';
import { useServices } from '../context/services/ServicesContext';
import { useAuth } from '../context/auth/CognitoAuthContext';
import { toast } from 'react-toastify';
import { hybridUploadService } from '../services/upload/hybridUploadService';

const InfluencerSettingsPage = () => {
  const { selectedInfluencer, setSelectedInfluencer } = useImages();
  const { influencerService } = useServices();
  const { isAuthenticated } = useAuth();
  const [influencers, setInfluencers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState('add'); // 'add' or 'edit'
  const [formData, setFormData] = useState({
    name: '',
    login: '',
    onlyfansLink: '',
    instagramLink: '',
    isVintage: false,
    referenceImage: '',
    prompt: '',
    nudityLevel: "2", // Default to moderate (2)
    promptPrefix: '',
    promptSuffix: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [influencerToDelete, setInfluencerToDelete] = useState(null);
  const [formUrl, setFormUrl] = useState('');
  const [showFormUrl, setShowFormUrl] = useState(false);
  const [generatingUrl, setGeneratingUrl] = useState(false);
  const [vintageAutoDetected, setVintageAutoDetected] = useState(false);
  
  // Reference image upload states
  const [uploadingReference, setUploadingReference] = useState(false);
  const [referenceFile, setReferenceFile] = useState(null);
  const [referencePreviewUrl, setReferencePreviewUrl] = useState('');
  
  // For tabs
  const { id } = useParams();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  
  // Tab labels and paths
  const tabs = [
    { label: 'About', path: 'about', icon: <PersonIcon /> },
    { label: 'Raw Dataset', path: 'raw-dataset', icon: <CollectionsIcon /> },
    { label: 'Cropped & Captioned', path: 'cropped-captioned', icon: <TextFieldsIcon /> },
    { label: 'Training Settings', path: 'training-settings', icon: <SettingsIcon /> }
  ];

  useEffect(() => {
    if (isAuthenticated) {
      fetchInfluencers();
    }
  }, [isAuthenticated]);

  // Effect to automatically detect "_throwback" suffix and activate vintage mode
  useEffect(() => {
    const modelName = formData.name.toLowerCase();
    const hasThrowbackSuffix = modelName.endsWith('_throwback');
    
    if (hasThrowbackSuffix && !formData.isVintage) {
      setFormData(prev => ({
        ...prev,
        isVintage: true
      }));
      setVintageAutoDetected(true);
    } else if (!hasThrowbackSuffix && vintageAutoDetected) {
      // Reset vintage auto-detection if user removes the suffix
      setVintageAutoDetected(false);
      // Don't automatically turn off vintage mode - let user decide
    }
  }, [formData.name, formData.isVintage, vintageAutoDetected]);
  
  // Set the selected influencer when the ID in the URL changes
  useEffect(() => {
    if (id && influencers.length > 0) {
      const influencer = influencers.find(inf => inf.id === id);
      if (influencer) {
        setSelectedInfluencer(influencer);
      }
    }
  }, [id, influencers, setSelectedInfluencer]);
  
  // Set the correct tab value based on the current URL path
  useEffect(() => {
    if (id) {
      const path = window.location.pathname.split('/').pop();
      const tabIndex = tabs.findIndex(tab => tab.path === path);
      if (tabIndex !== -1) {
        setTabValue(tabIndex);
        console.log(`Setting tab value to ${tabIndex} for path ${path}`);
      }
    }
  }, [id, tabs]);

  const fetchInfluencers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Use the new influencerService to get all influencers
      const data = await influencerService.getAll();
      setInfluencers(data);
    } catch (error) {
      console.error('Error fetching influencers:', error);
      setError('Failed to load influencers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddDialog = () => {
    setDialogMode('add');
    setFormData({
      name: '',
      login: '',
      onlyfansLink: '',
      instagramLink: '',
      isVintage: false,
      referenceImage: '',
      prompt: '',
      nudityLevel: "2", // Default to moderate (2)
      promptPrefix: '',
      promptSuffix: ''
    });
    setFormErrors({});
    setVintageAutoDetected(false);
    setReferenceFile(null);
    setReferencePreviewUrl('');
    setOpenDialog(true);
  };

  const handleOpenEditDialog = (influencer) => {
    setDialogMode('edit');
    // Convert nudity_level to string value for the form
    let nudityLevelValue = influencer.nudity_level;
    if (typeof nudityLevelValue === 'number') {
      // Database now stores 1-4 scale directly, no conversion needed
      nudityLevelValue = String(nudityLevelValue); // 1->1, 2->2, 3->3, 4->4
    } else if (typeof nudityLevelValue === 'string') {
      // Handle legacy string values
      const legacyNudityMap = { 'none': '1', 'mild': '2', 'moderate': '3', 'explicit': '4' };
      nudityLevelValue = legacyNudityMap[nudityLevelValue] || nudityLevelValue || '2';
    } else {
      nudityLevelValue = '2'; // Default to moderate
    }
    
    setFormData({
      id: influencer.id,
      name: influencer.models_name,
      login: influencer.models_login,
      onlyfansLink: influencer.onlyfans_link || '',
      instagramLink: influencer.instagram_link || '',
      isVintage: influencer.is_vintage || false,
      referenceImage: influencer.reference_image || '',
      prompt: influencer.prompt || '',
      nudityLevel: nudityLevelValue,
      promptPrefix: influencer.prompt_prefix || '',
      promptSuffix: influencer.prompt_suffix || ''
    });
    setFormErrors({});
    setVintageAutoDetected(false);
    setReferenceFile(null);
    setReferencePreviewUrl('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    // Clean up reference image preview
    if (referencePreviewUrl) {
      URL.revokeObjectURL(referencePreviewUrl);
      setReferencePreviewUrl('');
    }
    setReferenceFile(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Clear error for this field if it exists
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null
      });
    }
  };

  // Handle reference image file selection
  const handleReferenceImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate that it's an image
      if (!file.type.startsWith('image/')) {
        setFormErrors({
          ...formErrors,
          referenceImage: 'Please select a valid image file'
        });
        return;
      }
      
      setReferenceFile(file);
      setReferencePreviewUrl(URL.createObjectURL(file));
      
      // Clear any existing errors
      if (formErrors.referenceImage) {
        setFormErrors({
          ...formErrors,
          referenceImage: null
        });
      }
    }
  };

  // Clear reference image selection
  const handleClearReferenceImage = () => {
    setReferenceFile(null);
    if (referencePreviewUrl) {
      URL.revokeObjectURL(referencePreviewUrl);
      setReferencePreviewUrl('');
    }
  };

  // Upload reference image to S3
  const uploadReferenceImage = async (influencerId) => {
    if (!referenceFile) return null;
    
    setUploadingReference(true);
    
    try {
      const result = await hybridUploadService.uploadFile(
        referenceFile,
        influencerId,
        'reference', // Use 'reference' folder
        null, // No progress callback needed
        null, // No success callback needed
        null  // No error callback needed
      );
      
      if (result.success && result.data) {
        // Return the S3 URL from the upload result
        return result.data.url || result.data.s3_url || result.data.presignedUrl;
      } else {
        throw new Error(result.error || 'Failed to upload reference image');
      }
    } catch (error) {
      console.error('Error uploading reference image:', error);
      throw new Error('Failed to upload reference image: ' + error.message);
    } finally {
      setUploadingReference(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (!formData.login.trim()) {
      errors.login = 'Login is required';
    }
    
    // Only validate OnlyFans URL if provided (not required)
    if (formData.onlyfansLink && formData.onlyfansLink.trim() !== '' && !formData.onlyfansLink.includes('onlyfans.com')) {
      errors.onlyfansLink = 'Must be a valid OnlyFans URL';
    }
    
    // Only validate Instagram URL if provided (not required)
    if (formData.instagramLink && formData.instagramLink.trim() !== '' && !formData.instagramLink.includes('instagram.com')) {
      errors.instagramLink = 'Must be a valid Instagram URL';
    }
    
    // Reference image validation is handled by the upload process
    
    // Validate nudity level (required field with default)
    if (!['1', '2', '3', '4'].includes(formData.nudityLevel)) {
      errors.nudityLevel = 'Invalid nudity level selection';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    
    // 🔍 DEBUG: Enhanced form submission logging
    console.group('🚀 FORM SUBMISSION STARTED');
    console.log('Dialog Mode:', dialogMode);
    console.log('Original Form Data:', JSON.stringify(formData, null, 2));
    console.log('Reference File Selected:', !!referenceFile);
    if (referenceFile) {
      console.log('Reference File Details:', {
        name: referenceFile.name,
        size: referenceFile.size,
        type: referenceFile.type
      });
    }
    console.groupEnd();
    
    setLoading(true);
    setError(null);
    
    try {
      let referenceImageUrl = formData.referenceImage;
      
      // If there's a new reference image file to upload, do it first
      if (referenceFile) {
        if (dialogMode === 'add') {
          // For new influencers, we need to create them first to get an ID
          const tempData = {
            models_name: formData.name.trim(),
            models_login: formData.login.trim(),
            onlyfans_link: formData.onlyfansLink.trim() || null,
            instagram_link: formData.instagramLink.trim() || null,
            reference_image: null, // Will be updated after upload
            prompt: formData.prompt.trim() || null,
            nudity_level: formData.nudityLevel,
            prompt_prefix: formData.promptPrefix.trim() || null,
            prompt_suffix: formData.promptSuffix.trim() || null
          };
          
          console.group('📝 CREATING NEW INFLUENCER (Temp Data)');
          console.log('Temp Data for Creation:', JSON.stringify(tempData, null, 2));
          console.groupEnd();
          
          const newInfluencer = await influencerService.create(tempData);
          
          console.log('✅ New Influencer Created:', JSON.stringify(newInfluencer, null, 2));
          
          try {
            // Upload reference image to S3
            referenceImageUrl = await uploadReferenceImage(newInfluencer.id);
            
            // Update the influencer with the reference image URL
            const updatedData = { ...tempData, reference_image: referenceImageUrl };
            const finalInfluencer = await influencerAPI.update(newInfluencer.id, updatedData);
            
            setInfluencers([...influencers, finalInfluencer]);
            
            console.log('✅ Reference image upload completed for new influencer');
            // Don't close dialog automatically - let user decide
            toast.success('Influencer created with reference image successfully!');
            
            return; // Exit early since we handled the add case
          } catch (uploadError) {
            // If upload fails, still keep the influencer but without reference image
            console.error('Reference image upload failed:', uploadError);
            setInfluencers([...influencers, newInfluencer]);
            setError('Influencer created but reference image upload failed: ' + uploadError.message);
          }
        } else {
          // For editing existing influencers
          try {
            referenceImageUrl = await uploadReferenceImage(formData.id);
          } catch (uploadError) {
            console.error('Reference image upload failed:', uploadError);
            setError('Failed to upload reference image: ' + uploadError.message);
            setLoading(false);
            return;
          }
        }
      }
      
      // Prepare data for create/update
      const backendData = {
        models_name: formData.name.trim(),
        models_login: formData.login.trim(),
        onlyfans_link: formData.onlyfansLink.trim() || null,
        instagram_link: formData.instagramLink.trim() || null,
        reference_image: referenceImageUrl || null,
        prompt: formData.prompt.trim() || null,
        nudity_level: formData.nudityLevel,
        prompt_prefix: formData.promptPrefix.trim() || null,
        prompt_suffix: formData.promptSuffix.trim() || null
      };
      
      // 🔍 DEBUG: Final backend data preparation
      console.group('📤 FINAL BACKEND DATA PREPARATION');
      console.log('Backend Data Object:', JSON.stringify(backendData, null, 2));
      
      // Highlight the 5 new fields specifically
      const newFields = ['reference_image', 'prompt', 'nudity_level', 'prompt_prefix', 'prompt_suffix'];
      console.log('🎯 THE 5 NEW FIELDS STATUS:');
      newFields.forEach(field => {
        const value = backendData[field];
        const status = (value !== null && value !== undefined && value !== '') ? '✅ HAS VALUE' : '⚠️  EMPTY/NULL';
        console.log(`  ${field}: ${status} → ${JSON.stringify(value)}`);
      });
      console.groupEnd();
      
      let data;
      
      if (dialogMode === 'add' && !referenceFile) {
        console.log('🆕 CREATING NEW INFLUENCER (Simple Add)');
        // Simple add without reference image upload
        data = await influencerService.create(backendData);
        setInfluencers([...influencers, data]);
        
        console.log('✅ Create operation completed:', JSON.stringify(data, null, 2));
        
        // Skip form URL generation
        handleCloseDialog();
      } else if (dialogMode === 'edit') {
        console.log(`✏️  UPDATING EXISTING INFLUENCER (ID: ${formData.id})`);
        // Update existing influencer
        data = await influencerService.update(formData.id, backendData);
        setInfluencers(influencers.map(inf =>
          inf.id === formData.id ? data : inf
        ));
        
        console.log('✅ Update operation completed:', JSON.stringify(data, null, 2));
        
        // Update selected influencer if it's the one being edited
        if (selectedInfluencer && selectedInfluencer.id === formData.id) {
          setSelectedInfluencer(data);
        }
        
        handleCloseDialog();
      }
    } catch (error) {
      console.group('🚨 FORM SUBMISSION ERROR');
      console.error('Error saving influencer:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        formData: formData
      });
      console.groupEnd();
      
      setError('Failed to save influencer. Please try again.');
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };
  
  const handleGenerateFormUrl = async (influencerId) => {
    setGeneratingUrl(true);
    setError(null);
    
    try {
      const hashData = await influencerService.generateHash(influencerId);
      setFormUrl(hashData.url);
      setShowFormUrl(true);
    } catch (error) {
      console.error('Error generating form URL:', error);
      setError('Failed to generate form URL. Please try again.');
    } finally {
      setGeneratingUrl(false);
    }
  };
  
  const handleCopyFormUrl = () => {
    navigator.clipboard.writeText(formUrl)
      .then(() => {
        toast.success('Form URL copied to clipboard!');
      })
      .catch((error) => {
        console.error('Error copying form URL:', error);
        toast.error('Failed to copy form URL. Please try again.');
      });
  };
  
  const handleCloseFormUrl = () => {
    setShowFormUrl(false);
    setFormUrl('');
    handleCloseDialog();
  };

  const handleOpenDeleteConfirm = (influencer) => {
    setInfluencerToDelete(influencer);
    setDeleteConfirmOpen(true);
  };

  const handleCloseDeleteConfirm = () => {
    setDeleteConfirmOpen(false);
    setInfluencerToDelete(null);
  };

  const handleDelete = async () => {
    if (!influencerToDelete) return;
    
    setLoading(true);
    setError(null);
    
    try {
      await influencerService.delete(influencerToDelete.id);
      
      // Remove from list
      setInfluencers(influencers.filter(inf => inf.id !== influencerToDelete.id));
      
      // If this was the selected influencer, clear selection
      if (selectedInfluencer && selectedInfluencer.id === influencerToDelete.id) {
        setSelectedInfluencer(null);
        navigate('/');
      }
      
      handleCloseDeleteConfirm();
    } catch (error) {
      console.error('Error deleting influencer:', error);
      setError('Failed to delete influencer. Please try again.');
      handleCloseDeleteConfirm();
    } finally {
      setLoading(false);
    }
  };
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    if (selectedInfluencer) {
      navigate(`/influencer/${selectedInfluencer.id}/${tabs[newValue].path}`);
      console.log(`Navigating to tab: ${tabs[newValue].path}`);
    }
  };
  
  // If we're on an influencer detail page, show tabs and content
  if (id && selectedInfluencer) {
    return (
      <Container>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            {selectedInfluencer.models_name}
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Manage influencer data and settings
          </Typography>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            {tabs.map((tab, index) => (
              <Tab 
                key={tab.path} 
                label={tab.label} 
                icon={tab.icon} 
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Box>
        
        <Outlet />
      </Container>
    );
  }
  
  // Otherwise show the influencer list
  return (
    <Container>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Influencer Settings
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Manage influencer profiles and their social media links.
        </Typography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAddDialog}
          disabled={loading}
        >
          Add Influencer
        </Button>
      </Box>
      
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Login</TableCell>
              <TableCell>OnlyFans Link</TableCell>
              <TableCell>Instagram Link</TableCell>
              <TableCell>Nudity Level</TableCell>
              <TableCell>Reference Image</TableCell>
              <TableCell>Vintage</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && influencers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : influencers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No influencers found. Add your first influencer to get started.
                </TableCell>
              </TableRow>
            ) : (
              influencers.map((influencer) => (
                <TableRow 
                  key={influencer.id}
                  hover
                  onClick={() => navigate(`/influencer/${influencer.id}/about`)}
                  style={{ cursor: 'pointer' }}
                >
                  <TableCell>{influencer.models_name}</TableCell>
                  <TableCell>{influencer.models_login}</TableCell>
                  <TableCell>
                    {influencer.onlyfans_link ? (
                      <a
                        href={influencer.onlyfans_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {influencer.onlyfans_link}
                      </a>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {influencer.instagram_link ? (
                      <a
                        href={influencer.instagram_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {influencer.instagram_link}
                      </a>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const nudityLabels = {
                        '1': '1 - Mild',
                        '2': '2 - Moderate',
                        '3': '3 - Explicit',
                        '4': '4 - Extreme'
                      };
                      const nudityColors = {
                        '1': 'info',
                        '2': 'warning',
                        '3': 'error',
                        '4': 'error'
                      };
                      
                      let normalizedLevel;
                      let level = influencer.nudity_level;
                      
                      // Handle both legacy data and new string format
                      if (typeof level === 'number') {
                        // Database now stores 1-4 scale directly, no conversion needed
                        normalizedLevel = String(level); // 1->1, 2->2, 3->3, 4->4
                      } else if (typeof level === 'string') {
                        // Handle legacy string values and new string format
                        const legacyNudityMap = { 'none': '1', 'mild': '2', 'moderate': '3', 'explicit': '4' };
                        normalizedLevel = legacyNudityMap[level] || level;
                      } else {
                        normalizedLevel = '2'; // Default to moderate
                      }
                      
                      return (
                        <Chip
                          icon={<WarningIcon />}
                          label={nudityLabels[normalizedLevel] || '2 - Moderate'}
                          size="small"
                          color={nudityColors[normalizedLevel] || 'warning'}
                          variant="outlined"
                        />
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {influencer.reference_image ? (
                      <Chip
                        icon={<ImageIcon />}
                        label="Available"
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    {influencer.is_vintage ? (
                      <Chip
                        icon={<VintageIcon />}
                        label="Vintage"
                        size="small"
                        color="warning"
                        variant="outlined"
                      />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Generate Form URL">
                      <IconButton 
                        color="secondary" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleGenerateFormUrl(influencer.id);
                        }}
                        disabled={loading}
                      >
                        <LinkIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton 
                        color="primary" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenEditDialog(influencer);
                        }}
                        disabled={loading}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton 
                        color="error" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenDeleteConfirm(influencer);
                        }}
                        disabled={loading}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'add' ? 'Add New Influencer' : 'Edit Influencer'}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            name="name"
            label="Name"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.name}
            onChange={handleInputChange}
            error={!!formErrors.name}
            helperText={formErrors.name}
            disabled={loading}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="login"
            label="Login"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.login}
            onChange={handleInputChange}
            error={!!formErrors.login}
            helperText={formErrors.login}
            disabled={loading}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="onlyfansLink"
            label="OnlyFans Link"
            type="url"
            fullWidth
            variant="outlined"
            value={formData.onlyfansLink}
            onChange={handleInputChange}
            error={!!formErrors.onlyfansLink}
            helperText={formErrors.onlyfansLink}
            disabled={loading}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            name="instagramLink"
            label="Instagram Link"
            type="url"
            fullWidth
            variant="outlined"
            value={formData.instagramLink}
            onChange={handleInputChange}
            error={!!formErrors.instagramLink}
            helperText={formErrors.instagramLink}
            disabled={loading}
            sx={{ mb: 2 }}
          />
          
          {/* Reference Image Upload */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Reference Image (Optional)
            </Typography>
            <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', mb: 1 }}>
              Upload a reference image for the influencer. This will be stored in S3.
            </Typography>
            
            {/* Current reference image display */}
            {formData.referenceImage && !referenceFile && (
              <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="body2" gutterBottom>Current Reference Image:</Typography>
                <img
                  src={formData.referenceImage}
                  alt="Current reference"
                  style={{ maxHeight: '150px', maxWidth: '100%', objectFit: 'contain' }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'block';
                  }}
                />
                <Typography variant="caption" color="error" sx={{ display: 'none' }}>
                  Failed to load current reference image
                </Typography>
              </Box>
            )}
            
            {/* File upload section */}
            <Box sx={{
              border: '2px dashed',
              borderColor: referenceFile ? 'success.main' : 'primary.main',
              borderRadius: 1,
              p: 2,
              textAlign: 'center',
              bgcolor: referenceFile ? 'success.light' : 'transparent'
            }}>
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="reference-image-upload"
                type="file"
                onChange={handleReferenceImageChange}
                disabled={loading || uploadingReference}
              />
              
              {referencePreviewUrl && (
                <Box sx={{ mb: 2 }}>
                  <img
                    src={referencePreviewUrl}
                    alt="Reference preview"
                    style={{ maxHeight: '150px', maxWidth: '100%', objectFit: 'contain' }}
                  />
                </Box>
              )}
              
              <label htmlFor="reference-image-upload">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={referenceFile ? <ImageIcon /> : <ImageIcon />}
                  disabled={loading || uploadingReference}
                  color={referenceFile ? 'success' : 'primary'}
                >
                  {referenceFile ? `Selected: ${referenceFile.name}` : 'Choose Reference Image'}
                </Button>
              </label>
              
              {referenceFile && (
                <Box sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    color="error"
                    onClick={handleClearReferenceImage}
                    disabled={loading || uploadingReference}
                  >
                    Clear
                  </Button>
                </Box>
              )}
              
              {uploadingReference && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2">Uploading reference image...</Typography>
                </Box>
              )}
            </Box>
          </Box>
          
          {/* Prompt */}
          <TextField
            margin="dense"
            name="prompt"
            label="Prompt"
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            value={formData.prompt}
            onChange={handleInputChange}
            error={!!formErrors.prompt}
            helperText={formErrors.prompt || "Optional: Descriptive prompt for AI image generation"}
            disabled={loading}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <TextFieldsIcon />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />
          
          {/* Nudity Level */}
          <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
            <InputLabel id="nudity-level-label">Nudity Level</InputLabel>
            <Select
              labelId="nudity-level-label"
              name="nudityLevel"
              value={formData.nudityLevel}
              onChange={handleInputChange}
              error={!!formErrors.nudityLevel}
              disabled={loading}
              startAdornment={
                <InputAdornment position="start">
                  <WarningIcon />
                </InputAdornment>
              }
              label="Nudity Level"
            >
              <MenuItem value="1">1 - Mild</MenuItem>
              <MenuItem value="2">2 - Moderate</MenuItem>
              <MenuItem value="3">3 - Explicit</MenuItem>
              <MenuItem value="4">4 - Extreme</MenuItem>
            </Select>
            {formErrors.nudityLevel && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 2 }}>
                {formErrors.nudityLevel}
              </Typography>
            )}
          </FormControl>
          
          {/* Prompt Prefix */}
          <TextField
            margin="dense"
            name="promptPrefix"
            label="Prompt Prefix"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.promptPrefix}
            onChange={handleInputChange}
            error={!!formErrors.promptPrefix}
            helperText={formErrors.promptPrefix || "Optional: Text to add before the main prompt"}
            disabled={loading}
            sx={{ mb: 2 }}
          />
          
          {/* Prompt Suffix */}
          <TextField
            margin="dense"
            name="promptSuffix"
            label="Prompt Suffix"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.promptSuffix}
            onChange={handleInputChange}
            error={!!formErrors.promptSuffix}
            helperText={formErrors.promptSuffix || "Optional: Text to add after the main prompt"}
            disabled={loading}
            sx={{ mb: 2 }}
          />
          
          {/* Vintage Classification Toggle */}
          <Box sx={{ mt: 2, mb: 1 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isVintage}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      isVintage: e.target.checked
                    });
                  }}
                  name="isVintage"
                  color="warning"
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VintageIcon />
                  <Typography variant="body2">Vintage Classification</Typography>
                  {vintageAutoDetected && (
                    <Chip
                      icon={<AutoModeIcon />}
                      label="Auto-detected"
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  )}
                </Box>
              }
              disabled={loading}
            />
            
            {vintageAutoDetected && (
              <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
                <Typography variant="body2">
                  Vintage classification automatically activated because the name contains "_throwback" suffix.
                </Typography>
              </Alert>
            )}
            
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Enable this for influencers with a vintage or retro aesthetic. Names ending with "_throwback" will automatically activate this setting.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteConfirm}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {influencerToDelete?.models_name}? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteConfirm} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Form URL Dialog */}
      <Dialog
        open={showFormUrl}
        onClose={handleCloseFormUrl}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Influencer Form URL</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Share this URL with the influencer to allow them to fill out their information. The URL contains a secure hash and will expire in 30 days.
          </DialogContentText>
          
          {generatingUrl ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TextField
              fullWidth
              variant="outlined"
              value={formUrl}
              InputProps={{
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <Tooltip title="Copy URL">
                      <IconButton
                        edge="end"
                        onClick={handleCopyFormUrl}
                      >
                        <ContentCopyIcon />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ),
                startAdornment: (
                  <InputAdornment position="start">
                    <LinkIcon />
                  </InputAdornment>
                ),
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFormUrl}>
            Close
          </Button>
          <Button 
            onClick={handleCopyFormUrl} 
            variant="contained" 
            startIcon={<ContentCopyIcon />}
          >
            Copy URL
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default InfluencerSettingsPage;
